import React, { useMemo, useState } from 'react';
import { useCash } from '../CashContext';
import { WAKALA_CHANNELS, WAKALA_SERVICES_ALL, channelLabel, serviceLabel } from '../wakalaConstants';
import { downloadCsv, exportTableToPdf } from '../utils/reportExport';
import { getLocalDateKey } from '../utils/reportDates';
import './ReportPages.css';
import ReportFooter from '../components/ReportFooter';

const isMobileMoneyChannel = (channel) =>
  channel && channel !== 'cash' && channel !== 'card' && channel !== 'other' && channel !== 'other-services';

const ReportWakalaPage = ({ onNavigate }) => {
  const { transactions } = useCash();
  const today = getLocalDateKey();
  const monthStart = `${today.slice(0, 7)}-01`;

  const [tab, setTab] = useState('summary'); // summary | details
  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today);
  const [direction, setDirection] = useState('all');
  const [network, setNetwork] = useState('all');
  const [service, setService] = useState('all');
  const [mobileMoneyOnly, setMobileMoneyOnly] = useState(true);
  const [excludeCash, setExcludeCash] = useState(false);

  const filtered = useMemo(() => {
    return (transactions || []).filter((t) => {
      if (!t?.dateKey) return false;
      if (String(t.service || '') === 'other-services') return false;
      if (t.dateKey < fromDate || t.dateKey > toDate) return false;
      if (direction !== 'all' && t.direction !== direction) return false;
      if (network !== 'all' && t.channel !== network) return false;
      if (service !== 'all' && t.service !== service) return false;
      if (mobileMoneyOnly && !isMobileMoneyChannel(t.channel)) return false;
      if (excludeCash && t.channel === 'cash') return false;
      return true;
    });
  }, [transactions, fromDate, toDate, direction, network, service, mobileMoneyOnly, excludeCash]);

  const byNetwork = useMemo(() => {
    const map = new Map();
    for (const t of filtered) {
      const key = t.channel || 'unknown';
      if (!map.has(key)) {
        map.set(key, {
          channel: key,
          count: 0,
          cashInAmount: 0,
          cashOutAmount: 0,
          commission: 0,
        });
      }
      const row = map.get(key);
      row.count += 1;
      row.commission += Number(t.commission) || 0;
      if (t.direction === 'in') row.cashInAmount += Number(t.amount) || 0;
      if (t.direction === 'out') row.cashOutAmount += Number(t.amount) || 0;
    }
    return Array.from(map.values()).sort((a, b) => channelLabel(a.channel).localeCompare(channelLabel(b.channel)));
  }, [filtered]);

  const summaryGrand = useMemo(
    () =>
      byNetwork.reduce(
        (acc, r) => ({
          count: acc.count + r.count,
          cashIn: acc.cashIn + r.cashInAmount,
          cashOut: acc.cashOut + r.cashOutAmount,
          commission: acc.commission + r.commission,
        }),
        { count: 0, cashIn: 0, cashOut: 0, commission: 0 }
      ),
    [byNetwork]
  );

  const detailGrand = useMemo(
    () =>
      filtered.reduce(
        (acc, t) => ({
          amount: acc.amount + (Number(t.amount) || 0),
          commission: acc.commission + (Number(t.commission) || 0),
        }),
        { amount: 0, commission: 0 }
      ),
    [filtered]
  );

  const summaryRows = useMemo(
    () =>
      byNetwork.map((r) => [
        channelLabel(r.channel),
        r.count,
        r.cashInAmount,
        r.cashOutAmount,
        r.commission,
      ]),
    [byNetwork]
  );

  const summaryRowsForExport = useMemo(() => {
    const rows = [...summaryRows];
    if (byNetwork.length > 0) {
      rows.push(['TOTAL (all rows)', summaryGrand.count, summaryGrand.cashIn, summaryGrand.cashOut, summaryGrand.commission]);
    }
    return rows;
  }, [summaryRows, byNetwork.length, summaryGrand]);

  const detailRows = useMemo(
    () =>
      filtered.map((t) => [
        t.dateTime,
        t.direction === 'in' ? 'Cash-in' : 'Cash-out',
        channelLabel(t.channel),
        serviceLabel(t.service),
        Number(t.amount) || 0,
        Number(t.commission) || 0,
        t.createdBy,
        t.note || '',
        Number(t.floatAfter) || 0,
      ]),
    [filtered]
  );

  const detailRowsForExport = useMemo(() => {
    const rows = [...detailRows];
    if (filtered.length > 0) {
      rows.push(['TOTAL', '', '', '', detailGrand.amount, detailGrand.commission, '', '', '']);
    }
    return rows;
  }, [detailRows, filtered.length, detailGrand]);

  return (
    <div className="report-shell">
      <div className="report-back">
        <button type="button" onClick={() => onNavigate('reports')}>← All reports</button>
      </div>

      <div className="report-title-center">
        <h1>Agent cash reports</h1>
      </div>
      <p className="muted">From your cashbook.</p>

      <div className="report-toolbar">
        <div className="field">
          <label>From</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div className="field">
          <label>To</label>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
        <div className="field">
          <label>Direction</label>
          <select value={direction} onChange={(e) => setDirection(e.target.value)}>
            <option value="all">All</option>
            <option value="in">Cash-in</option>
            <option value="out">Cash-out</option>
          </select>
        </div>
        <div className="field">
          <label>Network</label>
          <select value={network} onChange={(e) => setNetwork(e.target.value)}>
            <option value="all">All networks</option>
            {WAKALA_CHANNELS.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Service</label>
          <select value={service} onChange={(e) => setService(e.target.value)}>
            <option value="all">All services</option>
            {WAKALA_SERVICES_ALL.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>View</label>
          <select value={tab} onChange={(e) => setTab(e.target.value)}>
            <option value="summary">By network (summary)</option>
            <option value="details">Details (line items)</option>
          </select>
        </div>
        <div className="field" style={{ minWidth: 220 }}>
          <label>Quick filters</label>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600, color: '#334155' }}>
              <input type="checkbox" checked={mobileMoneyOnly} onChange={(e) => setMobileMoneyOnly(e.target.checked)} />
              Mobile money only
            </label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600, color: '#334155' }}>
              <input type="checkbox" checked={excludeCash} onChange={(e) => setExcludeCash(e.target.checked)} />
              Exclude cash
            </label>
          </div>
        </div>

        <div className="report-actions">
          <button
            type="button"
            onClick={async () => {
              try {
                const title = `Wakala Report - ${fromDate}_to_${toDate}`;
                if (tab === 'summary') {
                  await exportTableToPdf(
                    title,
                    ['Network', 'Txns', 'Cash-in (TZS)', 'Cash-out (TZS)', 'Commission (TZS)'],
                    summaryRowsForExport,
                    {
                      subtitle: 'Network summary for wakala operations',
                      criteria: [
                        `Date range: ${fromDate} to ${toDate}`,
                        `Direction: ${direction}`,
                        `Network: ${network}`,
                        `Service: ${service}`,
                        `Mobile money only: ${mobileMoneyOnly ? 'Yes' : 'No'}`,
                        `Exclude cash: ${excludeCash ? 'Yes' : 'No'}`,
                      ],
                      summary: [
                        { label: 'Transactions', value: summaryGrand.count.toLocaleString() },
                        { label: 'Cash-in', value: `TZS ${Math.round(summaryGrand.cashIn).toLocaleString()}` },
                        { label: 'Cash-out', value: `TZS ${Math.round(summaryGrand.cashOut).toLocaleString()}` },
                        { label: 'Commission', value: `TZS ${Math.round(summaryGrand.commission).toLocaleString()}` },
                      ],
                    }
                  );
                } else {
                  await exportTableToPdf(
                    title,
                    ['Time', 'Type', 'Network', 'Service', 'Amount', 'Commission', 'By', 'Note', 'Float after'],
                    detailRowsForExport,
                    {
                      subtitle: 'Detailed transaction ledger',
                      criteria: [
                        `Date range: ${fromDate} to ${toDate}`,
                        `Direction: ${direction}`,
                        `Network: ${network}`,
                        `Service: ${service}`,
                        `Mobile money only: ${mobileMoneyOnly ? 'Yes' : 'No'}`,
                        `Exclude cash: ${excludeCash ? 'Yes' : 'No'}`,
                      ],
                      summary: [
                        { label: 'Rows', value: filtered.length.toLocaleString() },
                        { label: 'Amount total', value: `TZS ${Math.round(detailGrand.amount).toLocaleString()}` },
                        { label: 'Commission total', value: `TZS ${Math.round(detailGrand.commission).toLocaleString()}` },
                        { label: 'View mode', value: 'Details' },
                      ],
                    }
                  );
                }
              } catch (e) {
                console.error(e);
              }
            }}
          >
            Export PDF
          </button>
          <button
            type="button"
            onClick={() => {
              const base = `wakala-report_${fromDate}_to_${toDate}`;
              if (tab === 'summary') {
                downloadCsv(`${base}_summary.csv`, [['Network', 'Txns', 'Cash-in (TZS)', 'Cash-out (TZS)', 'Commission (TZS)'], ...summaryRowsForExport]);
              } else {
                downloadCsv(`${base}_details.csv`, [['Time', 'Type', 'Network', 'Service', 'Amount', 'Commission', 'By', 'Note', 'Float after'], ...detailRowsForExport]);
              }
            }}
          >
            Export Excel
          </button>
        </div>
      </div>

      <div className="report-grid-cards">
        <div className="report-stat"><span>Filtered transactions</span><strong>{filtered.length}</strong></div>
        <div className="report-stat">
          <span>Filtered cash-in</span>
          <strong>TZS {filtered.filter((t) => t.direction === 'in').reduce((s, t) => s + (Number(t.amount) || 0), 0).toLocaleString()}</strong>
        </div>
        <div className="report-stat">
          <span>Filtered cash-out</span>
          <strong>TZS {filtered.filter((t) => t.direction === 'out').reduce((s, t) => s + (Number(t.amount) || 0), 0).toLocaleString()}</strong>
        </div>
        <div className="report-stat">
          <span>Filtered commission</span>
          <strong>TZS {filtered.reduce((s, t) => s + (Number(t.commission) || 0), 0).toLocaleString()}</strong>
        </div>
      </div>

      {tab === 'summary' && (
        <div className="report-table-wrap">
          <h3>Totals by payment network</h3>
          <table className="report-table">
            <thead>
              <tr>
                <th>Network</th>
                <th>Transactions</th>
                <th>Cash-in (TZS)</th>
                <th>Cash-out (TZS)</th>
                <th>Commission (TZS)</th>
              </tr>
            </thead>
            <tbody>
              {byNetwork.length === 0 ? (
                <tr><td colSpan="5" style={{ textAlign: 'center', color: '#94a3b8' }}>No data for selected filters.</td></tr>
              ) : (
                byNetwork.map((r) => (
                  <tr key={r.channel}>
                    <td>{channelLabel(r.channel)}</td>
                    <td>{r.count}</td>
                    <td>{r.cashInAmount.toLocaleString()}</td>
                    <td>{r.cashOutAmount.toLocaleString()}</td>
                    <td>{r.commission.toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
            {byNetwork.length > 0 ? (
              <tfoot>
                <tr>
                  <td>
                    <strong>Total</strong>
                    <span className="report-table-totals-muted"> (all networks)</span>
                  </td>
                  <td>{summaryGrand.count.toLocaleString()}</td>
                  <td>{summaryGrand.cashIn.toLocaleString()}</td>
                  <td>{summaryGrand.cashOut.toLocaleString()}</td>
                  <td>{summaryGrand.commission.toLocaleString()}</td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      )}

      {tab === 'details' && (
        <div className="report-table-wrap">
          <h3>Line items</h3>
          <table className="report-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Type</th>
                <th>Network</th>
                <th>Service</th>
                <th>Amount</th>
                <th>Commission</th>
                <th>By</th>
                <th>Note</th>
                <th>Dro Δ</th>
                <th>Pair float Δ</th>
                <th>Dro after</th>
                <th>Float after</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="12" style={{ textAlign: 'center', color: '#94a3b8' }}>No data for selected filters.</td></tr>
              ) : (
                filtered.map((t) => (
                  <tr key={t.id}>
                    <td>{t.dateTime}</td>
                    <td>{t.direction === 'in' ? 'Cash-in' : 'Cash-out'}</td>
                    <td>{channelLabel(t.channel)}</td>
                    <td>{serviceLabel(t.service)}</td>
                    <td>{Number(t.amount).toLocaleString()}</td>
                    <td>{Number(t.commission).toLocaleString()}</td>
                    <td>{t.createdBy}</td>
                    <td>{t.note || '—'}</td>
                    <td className="report-num">
                      {t.physicalCashChange != null && Number.isFinite(Number(t.physicalCashChange))
                        ? `${Number(t.physicalCashChange) > 0 ? '+' : ''}${Number(t.physicalCashChange).toLocaleString()}`
                        : '—'}
                    </td>
                    <td className="report-num">
                      {t.networkFloatPairChange != null && Number.isFinite(Number(t.networkFloatPairChange))
                        ? `${Number(t.networkFloatPairChange) > 0 ? '+' : ''}${Number(t.networkFloatPairChange).toLocaleString()}`
                        : '—'}
                    </td>
                    <td className="report-num">
                      {t.physicalCashAfter != null ? Number(t.physicalCashAfter).toLocaleString() : '—'}
                    </td>
                    <td>{Number(t.floatAfter).toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
            {filtered.length > 0 ? (
              <tfoot>
                <tr>
                  <td colSpan={4}>
                    <strong>Totals</strong>
                    <span className="report-table-totals-muted"> (filtered)</span>
                  </td>
                  <td>{detailGrand.amount.toLocaleString()}</td>
                  <td>{detailGrand.commission.toLocaleString()}</td>
                  <td colSpan={6} />
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      )}
      <ReportFooter />
    </div>
  );
};

export default ReportWakalaPage;
